import { v4 as uuid } from 'uuid';

import { PromoRequestItem } from '~/components/Promo/hooks/usePromoRequests';

import { PromoLog } from '~/services/PromoService';

/** Log API may include request-style fields; typings omit them until contract is updated */
type PromoLogRow = PromoLog & {
  statusCode?: number | string;
  errorCode?: string;
  errorMsg?: string;
};

/** `null` = no parseable status on the log (UI may infer success); a number = recorded status. */
function httpStatusFromPromoLog(r: PromoLogRow): number | null {
  const raw = r.statusCode;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

/**
 * Parses promo logs into a standardized format of request items
 * @param promoLogs Array of promo logs to parse
 * @returns Array of standardized promo request items
 */
export const parsePromoRequestItemsFromPromoRequest = (promoLogs: Array<PromoLogRow>): Array<PromoRequestItem> => {
  return (
    promoLogs.flatMap((r) => {
      const items = r.details?.logResults?.inputOrder?.items || [];
      const deviceType = r.details?.logResults?.inputOrder?.deviceType;
      const statusCode = httpStatusFromPromoLog(r);
      const mappedItems = items.map((item) => ({
        ...item,
        uniqueKey: uuid(),
        requestAt: r.created_at,
        isDeleted: r.deleted ? true : false,
        deviceType,
        codeName: r.code_name,
        orderId: r.fk_orders,
        currencyCode: r.currency,
        userId: r.used_by,
        fullRequest: r.details?.logResults?.inputOrder,
        discountItem: r.details.discountItems.find((di) => di.item.reqItem.itemId === item.itemId) ?? null,
        statusCode,
        errorCode: r.errorCode,
        errorMsg: r.errorMsg,
      }));

      return mappedItems ?? [];
    }) ?? []
  );
};
